import React, { useEffect, useState } from "react";
import { getSiteKey, storage } from '../../firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { Container, Form, Button, Spinner, Alert } from "react-bootstrap";
import { SiteSettings, siteSettingsDefault } from "../../model/SiteSettingModel";
import { SettingGateway } from "../../gateways/SettingGateway";
import { Utils } from "../../common/Utils";
import HexColorPicker from '../common/HexColourPicker';
import { useGlobal } from "../../contexts/GlobalContext";
import HelpIcon from "../common/HelpIcon";

type Errors = {
    vatPercentage?: string;
    deliveryCost?: string;
    siteTitle?: string;
    paymentOptions?: string;
    loginProviders?: string;
    availableHours?: string;
    globalError?: string;
};

const SiteSetting: React.FC = () => {
    const [settings, setSettings] = useState<SiteSettings>(siteSettingsDefault);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [errors, setErrors] = useState<Errors>({});
    const orderedDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const paymentMethods = ['Cash', 'On-Collection', 'SnapScan', 'Zapper', 'Yoco'];
    const { site } = useGlobal();
    const [saveText, setSaveText] = useState<string>("");

    const generateCSS = (): string =>
        `/* Change Navbar background color */
.navbar {
  background: linear-gradient(to right, ${settings.colourSettings.Header.startColour}, ${settings.colourSettings.Header.endColour}) !important;
}

/* Adjust Navbar text color */
.navbar .navbar-brand,
.navbar .nav-link {
  color: ${settings.colourSettings.Header.linkColour} !important;
}

/* Change hover effect for links */
.navbar .nav-link:hover {
  color: ${settings.colourSettings.Header.linkHoverColour} !important; /* Lightened hover effect */
}

.btn-primary {
  background-color: ${settings.colourSettings.PrimaryButton.backgroundColour} !important; /* Override Bootstrap */
  border-color: ${settings.colourSettings.PrimaryButton.borderColour} !important;
  color: ${settings.colourSettings.PrimaryButton.textColour} !important; /* Adjust text color for contrast */
}

.btn-primary:hover {
  background-color: ${settings.colourSettings.PrimaryButton.hoverColour} !important; /* Darker shade on hover */
  border-color: ${settings.colourSettings.PrimaryButton.borderColour} !important;
}

.btn-outline-primary{  
  border-color: ${settings.colourSettings.PrimaryButton.borderColour} !important;
  color: ${settings.colourSettings.PrimaryButton.borderColour} !important; /* Adjust text color for contrast */
}

.btn-outline-primary:hover{  
  background-color: ${settings.colourSettings.PrimaryButton.backgroundColour} !important; /* Override Bootstrap */
  border-color: ${settings.colourSettings.PrimaryButton.borderColour} !important;
  color: ${settings.colourSettings.PrimaryButton.textColour} !important; /* Adjust text color for contrast */
}

.btn-secondary {
  background-color: ${settings.colourSettings.SecondaryButton.backgroundColour} !important; /* Override Bootstrap */
  border-color: ${settings.colourSettings.SecondaryButton.borderColour} !important;
  color: ${settings.colourSettings.SecondaryButton.textColour} !important; /* Adjust text color for contrast */
}

.btn-outline-secondary{  
  border-color: ${settings.colourSettings.SecondaryButton.borderColour} !important;
  color: ${settings.colourSettings.SecondaryButton.borderColour} !important; /* Adjust text color for contrast */
}

.btn-secondary:hover {
  background-color: ${settings.colourSettings.SecondaryButton.hoverColour} !important; /* Darker shade on hover */
  border-color: ${settings.colourSettings.SecondaryButton.borderColour} !important;
}

.btn-outline-secondary:hover{  
  background-color: ${settings.colourSettings.SecondaryButton.backgroundColour} !important; /* Override Bootstrap */
  border-color: ${settings.colourSettings.SecondaryButton.borderColour} !important;
  color: ${settings.colourSettings.SecondaryButton.textColour} !important; /* Adjust text color for contrast */
}

.navbar-toggler-icon {
  filter: invert(${Utils.IsColorLight(settings.colourSettings.Header.endColour) ? '0' : '1'});
}

.lucide-shopping-cart {  color: ${Utils.IsColorLight(settings.colourSettings.Header.endColour) ? "#575B5E" : "#B3B1AE"} !important;}

.navbar-toggler {
  border-color: ${Utils.IsColorLight(settings.colourSettings.Header.endColour) ? "#575B5E" : "#B3B1AE"} !important;
}
  
.navbar .btn-primary{
background-color: ${settings.colourSettings.PrimaryButton.backgroundColour} !important; /* Override Bootstrap */
color: ${settings.colourSettings.PrimaryButton.textColour} !important;
border-color: ${settings.colourSettings.PrimaryButton.borderColour} !important;
}

.navbar .btn-primary:hover{
background-color: ${settings.colourSettings.PrimaryButton.hoverColour} !important; /* Override Bootstrap */
color: ${settings.colourSettings.PrimaryButton.textColour} !important;
border-color: ${settings.colourSettings.PrimaryButton.borderColour} !important;
}

.pagination .page-item.active .page-link {
  background-color: ${settings.colourSettings.PrimaryButton.backgroundColour} !important; /* Dark gray */
  border-color: ${settings.colourSettings.PrimaryButton.borderColour} !important;
  color: ${settings.colourSettings.PrimaryButton.textColour} !important; /* Ensures contrast */
}
  
.list-group-item.active{
  background-color: ${settings.colourSettings.PrimaryButton.backgroundColour} !important;
  text-color: ${settings.colourSettings.PrimaryButton.textColour} !important;
  background: ${settings.colourSettings.PrimaryButton.backgroundColour} !important;
}
  
.dropdown-item:active{
  background-color: ${settings.colourSettings.PrimaryButton.backgroundColour} !important;
  text-color: ${settings.colourSettings.PrimaryButton.textColour} !important;
  background: ${settings.colourSettings.PrimaryButton.backgroundColour} !important;
}`;

    useEffect(() => {
        const fetchSettings = async () => {
            var settings = await SettingGateway.GetSettings();
            if (settings) {
                if (!settings.colourSettings) {
                    settings.colourSettings = siteSettingsDefault.colourSettings;
                }
                if (!settings.businessHours) {
                    settings.businessHours = siteSettingsDefault.businessHours;
                }
                setSettings(settings);
            }
            setLoading(false);
        };

        fetchSettings();
    }, []);

    const handleTimeChange = (day: string, field: "openTime" | "closeTime", value: string) => {
        setSettings(prev => ({ ...prev, businessHours: { ...prev.businessHours, [day]: { ...prev.businessHours[day], [field]: value }, } }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
        }));
    };

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };


    // Image upload state
    const [image, setImage] = useState<File | null>(null);
    const [uploading, setUploading] = useState<boolean>(false);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setImage(e.target.files[0]);
        }
    };

    const deleteImage = async () => {
        if (!settings.siteLogo) return;
        try {
            setUploading(true);
            const imageRef = ref(storage, settings.siteLogo);
            await deleteObject(imageRef);
            setSettings((prev) => ({ ...prev, siteLogo: '' }));
        } catch {
            alert('Error deleting image.');
        } finally {
            setUploading(false);
        }
    };

    const deleteSettingCache = async () => {
        const key = getSiteKey();
        const storageKey = `orderq-${key}`;
        sessionStorage.removeItem(storageKey);
    }

    const uploadImageAndSaveLogo = async () => {
        setUploading(true);
        if (!image) return;
        const storageRef = ref(storage, `siteLogo/${image.name}`);
        const uploadTask = uploadBytesResumable(storageRef, image);
        uploadTask.on(
            'state_changed',
            null,
            (error) => {
                setUploading(false);
                alert('Error uploading image.');
                console.error("Error saving the image", error);
            },
            async () => {
                const imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
                setSettings((prev) => ({ ...prev, siteLogo: imageUrl }));
                setImage(null);
                setUploading(false);
            }
        );
    };

    const handleSave = async () => {
        // Validation: If 'Cash' is selected, 'On-Collection' must also be selected
        let newErrors: Errors = {};

        if (!settings.vatPercentage) {
            newErrors.vatPercentage = "VAT Percentage is required";
        }

        if (!settings.siteTitle) {
            newErrors.siteTitle = "Site Title is required";
        }

        if (settings.allowDelivery) {
            if (!settings.deliveryCost) {
                newErrors.deliveryCost = "Delivery Cost is required";
            }
            else if (settings.deliveryCost < 0) {
                newErrors.deliveryCost = "Delivery Cost must be greater than or equal to 0.";
            }
        }

        if (!settings.checkoutPaymentOptions || settings.checkoutPaymentOptions.length === 0) {
            newErrors.paymentOptions = "At least one Available Payment Options at Checkout is required";
        }
        else if (settings.checkoutPaymentOptions.includes("Cash") && !settings.checkoutPaymentOptions.includes("On-Collection")) {
            newErrors.paymentOptions = "'On-Collection' must be selected if 'Cash' is selected as a payment option.";
        }

        if (!settings.allowEmailLogin && !settings.allowGoogleLogin && !settings.allowMicrosoftLogin) {
            newErrors.loginProviders = "At least one Login Provider is required"
        }

        if (!settings.receiveOrdersAlways && !settings.receiveOrdersLoggedIn && !settings.receiveOrdersDuringHours) {
            newErrors.availableHours = "At least one Receive orders when option is required"
        }

        if (Object.keys(newErrors).length !== 0) {
            newErrors.globalError = "Please fix all errors"
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            setSaving(true);
            setSettings(await SettingGateway.UpdateSettings({ ...settings, siteCSS: generateCSS() }));
            await deleteSettingCache();
            setSaveText('Saved Successfully');
            setTimeout(() => {
                setSaveText("");
                setSaving(false);
            }, 2000);
        }
    };

    return (
        <Container className="main-container mt-4">
            <h2>Website Settings</h2>
            {loading ? <Spinner animation="border" /> : (
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>VAT Percentage</Form.Label>
                        <Form.Control type="number" isInvalid={!!errors.vatPercentage} name="vatPercentage" value={settings.vatPercentage} onChange={handleChange} />
                        <Form.Control.Feedback type="invalid">{errors.vatPercentage}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Currency</Form.Label>
                        <Form.Select name="currency" value={settings.currency} onChange={handleSelectChange}>
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                            <option value="GBP">GBP</option>
                            <option value="ZAR">ZAR</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Site Title</Form.Label>
                        <Form.Control type="text" name="siteTitle" isInvalid={!!errors.siteTitle} value={settings.siteTitle} onChange={handleChange} />
                        <Form.Control.Feedback type="invalid">{errors.siteTitle}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Site Image</Form.Label>
                        {!settings.siteLogo && (
                            <>
                                <Form.Control type="file" accept="image/*" onChange={handleImageChange} disabled={uploading} />
                                <Button variant="secondary" className="mt-2" onClick={uploadImageAndSaveLogo} disabled={!image || uploading}>
                                    {uploading ? <Spinner animation="border" size="sm" /> : "Upload Image"}
                                </Button>
                            </>
                        )}
                        {settings.siteLogo && (
                            <div className='mt-2 mb-2'>
                                <img src={settings.siteLogo} alt="Site Logo" style={{ maxHeight: 40, objectFit: "cover", borderRadius: 8, border: "1px solid #ccc" }} />
                                <Button variant="danger" className="ms-2" onClick={deleteImage} disabled={uploading}>
                                    {uploading ? <Spinner animation="border" size="sm" /> : "Delete Image"}
                                </Button>
                            </div>
                        )}
                    </Form.Group>
                    {site && site.package !== "Starter" &&
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Site Header Colours&nbsp;
                                    <HelpIcon
                                        title="Site Header Styling"
                                        htmlContent={`
    <p>This allows you to customise the styles for your site header.</p>
    <ul>
      <li><strong>Start Colour</strong>: The background colour on the left side of the header.</li>
      <li><strong>End Colour</strong>: The background colour on the right side of the header.</br>
      <span style='font-style: italic;'>For single colour background, make the start and end colours the same.</span>
      <li><strong>Link Colour</strong>: The text colour of the menu links.</li>
      <li><strong>Link Hover Colour</strong>: The text colour when hovering over the menu links.</li>
    </ul>
  `}
                                        size={20}
                                        placement="right"
                                    />

                                </Form.Label>
                                <div className="d-flex flex-wrap gap-4">
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Label className="mb-0 ms-2">Start</Form.Label>
                                        <HexColorPicker
                                            initialColor={settings?.colourSettings.Header.startColour}
                                            onChange={(e) =>
                                                setSettings((prev) => ({
                                                    ...prev,
                                                    colourSettings: {
                                                        ...prev.colourSettings,
                                                        Header: { ...prev.colourSettings.Header, startColour: e }
                                                    }
                                                }))
                                            }
                                        />
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Label className="mb-0 ms-2">End</Form.Label>
                                        <HexColorPicker
                                            initialColor={settings.colourSettings.Header.endColour}
                                            onChange={(e) =>
                                                setSettings((prev) => ({
                                                    ...prev,
                                                    colourSettings: {
                                                        ...prev.colourSettings,
                                                        Header: { ...prev.colourSettings.Header, endColour: e }
                                                    }
                                                }))
                                            }
                                        />
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Label className="mb-0 ms-2">Link</Form.Label>
                                        <HexColorPicker
                                            initialColor={settings.colourSettings.Header.linkColour}
                                            onChange={(e) =>
                                                setSettings((prev) => ({
                                                    ...prev,
                                                    colourSettings: {
                                                        ...prev.colourSettings,
                                                        Header: { ...prev.colourSettings.Header, linkColour: e }
                                                    }
                                                }))
                                            }
                                        />
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Label className="mb-0 ms-2">Link Hover</Form.Label>
                                        <HexColorPicker
                                            initialColor={settings.colourSettings.Header.linkHoverColour}
                                            onChange={(e) =>
                                                setSettings((prev) => ({
                                                    ...prev,
                                                    colourSettings: {
                                                        ...prev.colourSettings,
                                                        Header: { ...prev.colourSettings.Header, linkHoverColour: e }
                                                    }
                                                }))
                                            }
                                        />
                                    </div>
                                </div>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Primary Button Colours&nbsp;
                                    <HelpIcon
                                        title="Primary Button Styling"
                                        htmlContent={`
    <p>This section allows you to customise the appearance of your primary button.</p>
    <ul>
      <li><strong>Background Colour</strong>: The default fill colour of the button.</li>
      <li><strong>Background Hover Colour</strong>: The fill colour when the user hovers over the button.</li>
      <li><strong>Border Colour</strong>: The colour of the button's border.</li>
      <li><strong>Text Colour</strong>: The colour of the button's label or text.</li>
    </ul>
  `}
                                        size={20}
                                        placement="right"
                                    />
                                </Form.Label>
                                <div className="d-flex flex-wrap gap-4">
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Label className="mb-0 ms-2">Background</Form.Label>
                                        <HexColorPicker
                                            initialColor={settings?.colourSettings.PrimaryButton.backgroundColour}
                                            onChange={(e) =>
                                                setSettings((prev) => ({
                                                    ...prev,
                                                    colourSettings: {
                                                        ...prev.colourSettings,
                                                        PrimaryButton: { ...prev.colourSettings.PrimaryButton, backgroundColour: e }
                                                    }
                                                }))
                                            }
                                        />
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Label className="mb-0 ms-2">Hover</Form.Label>
                                        <HexColorPicker
                                            initialColor={settings?.colourSettings.PrimaryButton.hoverColour}
                                            onChange={(e) =>
                                                setSettings((prev) => ({
                                                    ...prev,
                                                    colourSettings: {
                                                        ...prev.colourSettings,
                                                        PrimaryButton: { ...prev.colourSettings.PrimaryButton, hoverColour: e }
                                                    }
                                                }))
                                            }
                                        />
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Label className="mb-0 ms-2">Border</Form.Label>
                                        <HexColorPicker
                                            initialColor={settings?.colourSettings.PrimaryButton.borderColour}
                                            onChange={(e) =>
                                                setSettings((prev) => ({
                                                    ...prev,
                                                    colourSettings: {
                                                        ...prev.colourSettings,
                                                        PrimaryButton: { ...prev.colourSettings.PrimaryButton, borderColour: e }
                                                    }
                                                }))
                                            }
                                        />
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Label className="mb-0 ms-2">Text</Form.Label>
                                        <HexColorPicker
                                            initialColor={settings?.colourSettings.PrimaryButton.textColour}
                                            onChange={(e) =>
                                                setSettings((prev) => ({
                                                    ...prev,
                                                    colourSettings: {
                                                        ...prev.colourSettings,
                                                        PrimaryButton: { ...prev.colourSettings.PrimaryButton, textColour: e }
                                                    }
                                                }))
                                            }
                                        />
                                    </div>
                                </div>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Secondary Button Colours&nbsp;
                                    <HelpIcon
                                        title="Primary Button Styling"
                                        htmlContent={`
    <p>This section allows you to customise the appearance of your secondary button.</p>
    <ul>
      <li><strong>Background Colour</strong>: The default fill colour of the button.</li>
      <li><strong>Background Hover Colour</strong>: The fill colour when the user hovers over the button.</li>
      <li><strong>Border Colour</strong>: The colour of the button's border.</li>
      <li><strong>Text Colour</strong>: The colour of the button's label or text.</li>
    </ul>
  `}
                                        size={20}
                                        placement="right"
                                    />

                                </Form.Label>
                                <div className="d-flex flex-wrap gap-4">
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Label className="mb-0 ms-2">Background</Form.Label>
                                        <HexColorPicker
                                            initialColor={settings?.colourSettings.SecondaryButton.backgroundColour}
                                            onChange={(e) =>
                                                setSettings((prev) => ({
                                                    ...prev,
                                                    colourSettings: {
                                                        ...prev.colourSettings,
                                                        SecondaryButton: { ...prev.colourSettings.SecondaryButton, backgroundColour: e }
                                                    }
                                                }))
                                            }
                                        />
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Label className="mb-0 ms-2">Hover</Form.Label>
                                        <HexColorPicker
                                            initialColor={settings?.colourSettings.SecondaryButton.hoverColour}
                                            onChange={(e) =>
                                                setSettings((prev) => ({
                                                    ...prev,
                                                    colourSettings: {
                                                        ...prev.colourSettings,
                                                        SecondaryButton: { ...prev.colourSettings.SecondaryButton, hoverColour: e }
                                                    }
                                                }))
                                            }
                                        />
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Label className="mb-0 ms-2">Border</Form.Label>
                                        <HexColorPicker
                                            initialColor={settings?.colourSettings.SecondaryButton.borderColour}
                                            onChange={(e) =>
                                                setSettings((prev) => ({
                                                    ...prev,
                                                    colourSettings: {
                                                        ...prev.colourSettings,
                                                        SecondaryButton: { ...prev.colourSettings.SecondaryButton, borderColour: e }
                                                    }
                                                }))
                                            }
                                        />
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Label className="mb-0 ms-2">Text</Form.Label>
                                        <HexColorPicker
                                            initialColor={settings?.colourSettings.SecondaryButton.textColour}
                                            onChange={(e) =>
                                                setSettings((prev) => ({
                                                    ...prev,
                                                    colourSettings: {
                                                        ...prev.colourSettings,
                                                        SecondaryButton: { ...prev.colourSettings.SecondaryButton, textColour: e }
                                                    }
                                                }))
                                            }
                                        />
                                    </div>
                                </div>
                            </Form.Group>
                        </>}
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            label="Show Price"
                            name="hasPrice"
                            checked={settings.hasPrice}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            label="Allow Delivery"
                            name="allowDelivery"
                            checked={settings.allowDelivery}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Delivery Cost</Form.Label>
                        <Form.Control type="number" name="deliveryCost" isInvalid={!!errors.deliveryCost} disabled={!settings.allowDelivery} value={settings.deliveryCost} onChange={handleChange} />
                        <Form.Control.Feedback type="invalid">{errors.deliveryCost}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Available Payment Options at Checkout</Form.Label>
                        <Form.Select
                            multiple
                            isInvalid={!!errors.paymentOptions}
                            name="checkoutPaymentOptions"
                            value={settings.checkoutPaymentOptions}
                            onChange={(e) => {
                                const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                                setSettings((prev) => ({ ...prev, checkoutPaymentOptions: selected }));
                            }}
                        >
                            {paymentMethods.map((method) => (
                                <option key={method} value={method} disabled={(!settings.checkoutPaymentOptionsEnabled || settings.checkoutPaymentOptionsEnabled.indexOf(method) < 0) && method !== "On-Collection" && method !== "Cash"}>
                                    {method}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.paymentOptions}</Form.Control.Feedback>
                        <Form.Text>Select one or more options using Ctrl (or Cmd on Mac). The payment options needs to be activated before it can be selected.</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Allow following login providers:</Form.Label>
                        <Form.Check
                            type="checkbox"
                            label="Email and password login"
                            name="allowEmailLogin"
                            checked={settings.allowEmailLogin}
                            onChange={handleChange}
                        /> <span style={{ fontStyle: "italic" }}>Admin users can login via {Utils.GetFullUrl("/admin")}</span>
                        <Form.Check
                            type="checkbox"
                            label="Google login"
                            name="allowGoogleLogin"
                            checked={settings.allowGoogleLogin}
                            onChange={handleChange}
                        />

                        <Form.Check
                            type="checkbox"
                            label="Microsoft login"
                            name="allowMicrosoftLogin"
                            checked={settings.allowMicrosoftLogin}
                            onChange={handleChange}
                        />
                        {errors.loginProviders && <div className="text-danger mt-1">{errors.loginProviders}</div>}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Receive orders when:</Form.Label>
                        <Form.Check
                            type="checkbox"
                            label="Always"
                            name="receiveOrdersAlways"
                            checked={settings.receiveOrdersAlways}
                            onChange={(e) => {
                                setSettings(prev => ({
                                    ...prev, receiveOrdersAlways: (e.target as HTMLInputElement).checked, receiveOrdersDuringHours: false, receiveOrdersLoggedIn: false
                                }));
                            }}
                        />
                        <Form.Check
                            type="checkbox"
                            label="Store user is logged In"
                            name="receiveOrdersLoggedIn"
                            checked={settings.receiveOrdersLoggedIn}
                            onChange={(e) => {
                                setSettings(prev => ({
                                    ...prev, receiveOrdersAlways: false, receiveOrdersLoggedIn: (e.target as HTMLInputElement).checked
                                }));
                            }}
                        />

                        <Form.Check
                            type="checkbox"
                            label="During certain hours"
                            name="receiveOrdersDuringHours"
                            checked={settings.receiveOrdersDuringHours}
                            onChange={(e) => {
                                setSettings(prev => ({
                                    ...prev, receiveOrdersAlways: false, receiveOrdersDuringHours: (e.target as HTMLInputElement).checked
                                }));
                            }}
                        />
                        {errors.availableHours && <div className="text-danger mt-1">{errors.availableHours}</div>}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        {orderedDays.map(day => (
                            <div key={day} className="d-flex align-items-center mb-2">
                                <div className="day-label me-3">{day}</div>
                                <Form.Control
                                    type="time"
                                    value={settings.businessHours ? settings.businessHours[day].openTime : ""}
                                    onChange={(e) => handleTimeChange(day, "openTime", e.target.value)}
                                    className="me-2 flex-grow-1"
                                    disabled={!settings.receiveOrdersDuringHours || settings.receiveOrdersAlways}
                                />
                                <span className="me-2">to</span>
                                <Form.Control
                                    type="time"
                                    value={settings.businessHours ? settings.businessHours[day].closeTime : ""}
                                    onChange={(e) => handleTimeChange(day, "closeTime", e.target.value)}
                                    className="flex-grow-1"
                                    disabled={!settings.receiveOrdersDuringHours || settings.receiveOrdersAlways}
                                />
                            </div>
                        ))}
                    </Form.Group>

                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner animation="border" size="sm" /> : "Save Settings"}
                    </Button>
                    {saveText && <Alert variant="success" className="mb-0 mt-1 py-1 px-2">
                        {saveText}
                    </Alert>}
                    {errors.globalError && <Alert variant="danger" className="mb-0 mt-1 py-1 px-2">
                        {errors.globalError}
                    </Alert>}
                </Form>
            )}
        </Container>
    );
};

export default SiteSetting;